/**
 * Phase 6 — Search Panel — PRD §5.8
 *
 * Global slide-in search panel (400px from right). Opened from the header
 * search button. Results click → navigate to Folder View with signal selected.
 * Real-time search with 300ms debounce; filter options loaded once per session.
 */

import { api } from "../lib/api";
import { navigate } from "../lib/router";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SearchSignal {
  id: string;
  title: string;
  source: string;
  datePublished: string;
  wikilinks: string[];
  commentCount: number;
}

interface Workstream {
  id: string;
  name: string;
}

interface WikilinkItem {
  id: string;
  name: string;
  signalCount: number;
}

// ---------------------------------------------------------------------------
// Module state (singleton — panel is attached to document.body)
// ---------------------------------------------------------------------------

let _panel: HTMLElement | null = null;
let _overlay: HTMLElement | null = null;
let _debounceTimer: ReturnType<typeof setTimeout> | null = null;

// Filter data cached for the session lifetime
let _allWikilinks: WikilinkItem[] = [];
let _allWorkstreams: Workstream[] = [];
let _filtersLoaded = false;

// Wikilink to pre-select when the panel opens (set before openSearchPanel call)
let _pendingWikilink: string | null = null;

// ---------------------------------------------------------------------------
// Open / close
// ---------------------------------------------------------------------------

/** Open the panel pre-filtered by a specific wikilink and run the search. */
export function openSearchPanelWithWikilink(name: string): void {
  _pendingWikilink = name;
  openSearchPanel();
  // If filters were already loaded from a previous open, apply immediately
  if (_filtersLoaded) _applyPendingWikilink();
}

export function openSearchPanel(): void {
  if (_panel) {
    // Panel already open — just focus the input
    _panel.querySelector<HTMLInputElement>(".sp-search-input")?.focus();
    return;
  }

  _overlay = document.createElement("div");
  _overlay.className = "sp-overlay";
  _overlay.addEventListener("click", closeSearchPanel);

  _panel = document.createElement("div");
  _panel.className = "sp-panel";
  _panel.setAttribute("role", "dialog");
  _panel.setAttribute("aria-label", "Search signals");
  _panel.innerHTML = buildPanelHTML();

  document.body.appendChild(_overlay);
  document.body.appendChild(_panel);

  // Trigger CSS slide-in transition
  requestAnimationFrame(() => {
    _overlay!.classList.add("sp-overlay--open");
    _panel!.classList.add("sp-panel--open");
  });

  // Wire close button
  _panel.querySelector(".sp-close-btn")!.addEventListener("click", closeSearchPanel);

  // Wire Escape key
  document.addEventListener("keydown", _handleKeydown);

  // Wire search input (real-time, debounced)
  const input = _panel.querySelector<HTMLInputElement>(".sp-search-input")!;
  input.addEventListener("input", _scheduleSearch);

  // Wire filter apply button
  _panel.querySelector(".sp-apply-btn")?.addEventListener("click", () => { void _runSearch(); });

  // Render filter options from cache or fetch fresh
  if (!_filtersLoaded) {
    void _loadFilterOptions();
  } else {
    _renderWlChecklist();
    _renderWsChecklist();
  }

  input.focus();
}

function closeSearchPanel(): void {
  document.removeEventListener("keydown", _handleKeydown);
  if (_debounceTimer) {
    clearTimeout(_debounceTimer);
    _debounceTimer = null;
  }
  _overlay?.classList.remove("sp-overlay--open");
  _panel?.classList.remove("sp-panel--open");
  // Remove from DOM after transition completes
  setTimeout(() => {
    _panel?.remove();
    _overlay?.remove();
    _panel = null;
    _overlay = null;
  }, 260);
}

function _handleKeydown(e: KeyboardEvent): void {
  if (e.key === "Escape") closeSearchPanel();
}

// ---------------------------------------------------------------------------
// Panel HTML skeleton
// ---------------------------------------------------------------------------

function buildPanelHTML(): string {
  return `
    <div class="sp-header">
      <h2>Search</h2>
      <button class="icon-btn sp-close-btn" aria-label="Close search">✕</button>
    </div>
    <div class="sp-search-wrap">
      <input
        type="text"
        class="sp-search-input"
        placeholder="Search signals…"
        autocomplete="off"
        aria-label="Search query"
      />
    </div>
    <details class="sp-filters">
      <summary class="sp-filters-summary">Filters</summary>
      <div class="sp-filters-body">
        <div class="sp-filter-group">
          <span class="sp-filter-label">From</span>
          <input type="date" class="sp-date-input" id="sp-from" />
        </div>
        <div class="sp-filter-group">
          <span class="sp-filter-label">To</span>
          <input type="date" class="sp-date-input" id="sp-to" />
        </div>
        <div class="sp-filter-group">
          <span class="sp-filter-label">Sources</span>
          <input
            type="text"
            class="sp-text-input"
            id="sp-sources"
            placeholder="e.g. BBC, MIT"
          />
        </div>
        <div class="sp-filter-group">
          <span class="sp-filter-label">STEEPLES</span>
          <div class="sp-steeples-row">
            <select class="sp-steeples-select" id="sp-steeples-dim">
              <option value="">Any</option>
              <option value="social">Social</option>
              <option value="technological">Technological</option>
              <option value="economic">Economic</option>
              <option value="environmental">Environmental</option>
              <option value="political">Political</option>
              <option value="legal">Legal</option>
              <option value="ethical">Ethical</option>
              <option value="security">Security</option>
            </select>
            <input
              type="number"
              class="sp-steeples-min"
              id="sp-steeples-min"
              min="0"
              max="10"
              placeholder="≥"
            />
          </div>
        </div>
        <div class="sp-filter-group sp-filter-full-col">
          <span class="sp-filter-label">Wikilinks</span>
          <div class="sp-checklist" id="sp-wl-list">
            <span class="sp-checklist-loading">Loading…</span>
          </div>
        </div>
        <div class="sp-filter-group sp-filter-full-col">
          <span class="sp-filter-label">Workstreams</span>
          <div class="sp-checklist" id="sp-ws-list">
            <span class="sp-checklist-loading">Loading…</span>
          </div>
        </div>
        <div class="sp-filter-apply sp-filter-full-col">
          <button class="primary sp-apply-btn" style="width:100%;font-size:13px">
            Apply Filters &amp; Search
          </button>
        </div>
      </div>
    </details>
    <div class="sp-body" id="sp-results">
      <p class="sp-status">Type to search…</p>
    </div>`;
}

// ---------------------------------------------------------------------------
// Filter options
// ---------------------------------------------------------------------------

async function _loadFilterOptions(): Promise<void> {
  _filtersLoaded = true; // optimistic — prevents double-fetch
  try {
    const [wikilinks, workstreams] = await Promise.all([
      api<WikilinkItem[]>("/wikilinks"),
      api<Workstream[]>("/workstreams"),
    ]);
    _allWikilinks = wikilinks;
    _allWorkstreams = workstreams;
    _renderWlChecklist();
    _renderWsChecklist();
  } catch (_err) {
    if (_panel) {
      const wlList = _panel.querySelector("#sp-wl-list");
      const wsList = _panel.querySelector("#sp-ws-list");
      const na = `<span class="sp-checklist-loading">Unavailable</span>`;
      if (wlList) wlList.innerHTML = na;
      if (wsList) wsList.innerHTML = na;
    }
  }
}

function _renderWlChecklist(): void {
  if (!_panel) return;
  const el = _panel.querySelector<HTMLDivElement>("#sp-wl-list");
  if (!el) return;
  if (!_allWikilinks.length) {
    el.innerHTML = `<span class="sp-checklist-loading">No wikilinks</span>`;
    return;
  }
  el.innerHTML = _allWikilinks
    .slice(0, 60) // cap at 60 to keep panel manageable
    .map(
      (wl) =>
        `<label class="sp-check-item">
          <input type="checkbox" value="${_esc(wl.name)}" />
          <span class="sp-check-label">${_esc(wl.name)}</span>
          <span style="font-size:10px;color:var(--text-muted)">${wl.signalCount}</span>
        </label>`
    )
    .join("");

  // If a wikilink was requested via openSearchPanelWithWikilink, apply it now
  if (_pendingWikilink) _applyPendingWikilink();
}

/**
 * Check the pending wikilink in the filter list, expand the filters section,
 * and fire the search. Called once the checklist DOM is ready.
 */
function _applyPendingWikilink(): void {
  if (!_pendingWikilink || !_panel) return;
  const name = _pendingWikilink;
  _pendingWikilink = null;

  // Expand the Filters accordion so the user can see what was applied
  const details = _panel.querySelector<HTMLDetailsElement>(".sp-filters");
  if (details) details.open = true;

  // Check the matching wikilink checkbox
  const checkboxes = _panel.querySelectorAll<HTMLInputElement>("#sp-wl-list input[type='checkbox']");
  for (const cb of checkboxes) {
    if (cb.value === name) { cb.checked = true; break; }
  }

  // Run the search immediately
  void _runSearch();
}

function _renderWsChecklist(): void {
  if (!_panel) return;
  const el = _panel.querySelector<HTMLDivElement>("#sp-ws-list");
  if (!el) return;
  if (!_allWorkstreams.length) {
    el.innerHTML = `<span class="sp-checklist-loading">No workstreams</span>`;
    return;
  }
  el.innerHTML = _allWorkstreams
    .map(
      (ws) =>
        `<label class="sp-check-item">
          <input type="checkbox" value="${_esc(ws.id)}" />
          <span class="sp-check-label">${_esc(ws.name)}</span>
        </label>`
    )
    .join("");
}

// ---------------------------------------------------------------------------
// Search execution
// ---------------------------------------------------------------------------

function _scheduleSearch(): void {
  if (_debounceTimer) clearTimeout(_debounceTimer);
  _debounceTimer = setTimeout(() => { void _runSearch(); }, 300);
}

async function _runSearch(): Promise<void> {
  if (!_panel) return;

  const q = (_panel.querySelector<HTMLInputElement>(".sp-search-input")?.value ?? "").trim();
  const from = (_panel.querySelector<HTMLInputElement>("#sp-from")?.value ?? "").trim();
  const to = (_panel.querySelector<HTMLInputElement>("#sp-to")?.value ?? "").trim();
  const sources = (_panel.querySelector<HTMLInputElement>("#sp-sources")?.value ?? "").trim();
  const steeplesDim = (_panel.querySelector<HTMLSelectElement>("#sp-steeples-dim")?.value ?? "").trim();
  const steeplesMin = (_panel.querySelector<HTMLInputElement>("#sp-steeples-min")?.value ?? "").trim();

  const wlChecked = Array.from(
    _panel.querySelectorAll<HTMLInputElement>("#sp-wl-list input:checked")
  ).map((cb) => cb.value);

  const wsChecked = Array.from(
    _panel.querySelectorAll<HTMLInputElement>("#sp-ws-list input:checked")
  ).map((cb) => cb.value);

  // Require at least a query or filter to run
  if (!q && !from && !to && !sources && !steeplesDim && !wlChecked.length && !wsChecked.length) {
    _setResults(null, "Type to search or apply filters…");
    return;
  }

  _setResults(null, "Searching…");

  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  if (sources) params.set("sources", sources);
  if (steeplesDim) params.set("steeples_dim", steeplesDim);
  if (steeplesMin) params.set("steeples_min", steeplesMin);
  if (wlChecked.length) params.set("wikilinks", wlChecked.join(","));
  if (wsChecked.length) params.set("workstreams", wsChecked.join(","));

  try {
    const results = await api<SearchSignal[]>(`/search?${params.toString()}`);
    _setResults(results, results.length === 0 ? "No results found." : null);
  } catch (err) {
    _setResults(null, `Search failed.${err instanceof Error ? " " + err.message : ""}`);
  }
}

function _setResults(signals: SearchSignal[] | null, status: string | null): void {
  if (!_panel) return;
  const body = _panel.querySelector<HTMLDivElement>("#sp-results");
  if (!body) return;

  if (!signals || signals.length === 0) {
    body.innerHTML = `<p class="sp-status">${status ?? ""}</p>`;
    return;
  }

  body.innerHTML = "";
  for (const sig of signals) {
    const item = document.createElement("div");
    item.className = "sp-result-item";

    const wlHtml = sig.wikilinks
      .slice(0, 5)
      .map((wl) => `<span class="sp-result-wl-chip">${_esc(wl)}</span>`)
      .join("");

    item.innerHTML = `
      <div class="sp-result-title">${_esc(sig.title)}</div>
      <div class="sp-result-meta">
        ${_esc(sig.source)} · ${_esc(sig.datePublished)}
        <span class="sp-comment-count">Comments: ${sig.commentCount ?? 0}</span>
      </div>
      ${wlHtml ? `<div class="sp-result-wl">${wlHtml}</div>` : ""}`;

    item.addEventListener("click", () => {
      closeSearchPanel();
      navigate(`/folders?signal=${sig.id}`);
    });

    body.appendChild(item);
  }
}

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

function _esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
